// src/context/ThemeProvider.tsx - FINAL CLEAN VERSION
import { useState, useLayoutEffect, ReactNode } from "react";
import { ThemeContext, ThemeContextType } from "@/contexts/ThemeContext";
import { THEMES, DEFAULT_THEME_NAME, THEME_METADATA } from "@/themes";
import { type Theme } from "@/themes/types";

// Get a list of all known theme class names
const allThemeNames = THEME_METADATA.map((t) => t.name);

// Helper function to apply theme variables
const applyTheme = (themeName: string) => {
  const root = window.document.documentElement;
  const themeObject: Theme = THEMES[themeName] || THEMES[DEFAULT_THEME_NAME];

  // Remove all old theme classes
  root.classList.remove(...allThemeNames);

  // Add the new theme class
  root.classList.add(themeName);

  // Map themeObject keys to the CSS variables Tailwind expects
  // Using only the "light" variants for all themes since we removed dark/light mode
  const themeMap = {
    "--color-background": themeObject["background-light"],
    "--color-card": themeObject["card-light"],
    "--color-input": themeObject["input-light"],
    "--color-border": themeObject["border-light"],
    "--color-primary": themeObject["primary"],
    "--color-primary-hover": themeObject["primary-hover"],
    "--color-text": themeObject["text"],
    "--color-text-secondary": themeObject["text-secondary"],
  };

  // Apply the CSS variables to the :root element
  for (const [key, value] of Object.entries(themeMap)) {
    root.style.setProperty(key, value);
  }
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState(DEFAULT_THEME_NAME);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize theme from storage on mount
  useLayoutEffect(() => {
    let storedTheme: string | null = null;
    try {
      storedTheme = localStorage.getItem("theme");
    } catch (e) {
      console.warn("Failed to read theme from localStorage:", e);
    }

    const initialTheme =
      storedTheme && allThemeNames.includes(storedTheme)
        ? storedTheme
        : DEFAULT_THEME_NAME;

    setTheme(initialTheme);
    applyTheme(initialTheme);
    setIsInitialized(true);
  }, []);

  // Effect to apply theme when 'theme' state changes
  useLayoutEffect(() => {
    if (!isInitialized) return;

    applyTheme(theme);

    try {
      localStorage.setItem("theme", theme);
    } catch (error) {
      console.warn("Failed to save theme to localStorage:", error);
    }
  }, [theme, isInitialized]);

  const handleSetTheme = (newTheme: string) => {
    if (allThemeNames.includes(newTheme)) {
      setTheme(newTheme);
    } else {
      console.warn(
        `Attempted to set unsupported theme: ${newTheme}. Falling back to default.`,
      );
      setTheme(DEFAULT_THEME_NAME);
    }
  };

  const contextValue: ThemeContextType = {
    theme,
    setTheme: handleSetTheme,
    availableThemes: THEME_METADATA,
  };

  // Render nothing until theme is initialized to prevent flash of unstyled content
  if (!isInitialized) {
    return null;
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
